import React, { useEffect, useRef } from 'react';

const FLAG_SRC = `${process.env.PUBLIC_URL || ''}/mes-patrio/flag-mexico.png?v=47`;

export default function MexicoWavingFlag() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return undefined;

    const ctx = canvas.getContext('2d', { alpha: true });
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const img = new Image();
    img.src = FLAG_SRC;

    let raf = 0;
    let running = true;
    let startAt = 0;

    const layout = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      const nextW = Math.max(1, Math.round(width * dpr));
      const nextH = Math.max(1, Math.round(height * dpr));
      if (canvas.width === nextW && canvas.height === nextH) return;
      canvas.width = nextW;
      canvas.height = nextH;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    };

    const paint = (now) => {
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      if (!width || !height || !img.naturalWidth) return;

      const t = reduceMotion.matches ? 0 : (now - startAt) / 1000;
      ctx.clearRect(0, 0, width, height);

      const padY = height * 0.1;
      const maxH = height - padY * 2;
      const poleW = Math.max(7, maxH * 0.032);
      const maxW = width - poleW - 4;
      let flagH = maxH;
      let flagW = flagH * (7 / 4);
      if (flagW > maxW) {
        flagW = maxW;
        flagH = flagW * (4 / 7);
      }
      const top = (height - flagH) / 2;

      const poleGrad = ctx.createLinearGradient(0, 0, poleW, 0);
      poleGrad.addColorStop(0, '#5a4620');
      poleGrad.addColorStop(0.42, '#f0e0b0');
      poleGrad.addColorStop(0.7, '#c4a45c');
      poleGrad.addColorStop(1, '#4a3a18');
      ctx.fillStyle = poleGrad;
      ctx.fillRect(0, height * 0.02, poleW, height * 0.96);

      ctx.beginPath();
      ctx.arc(poleW / 2, height * 0.028, poleW * 0.72, 0, Math.PI * 2);
      ctx.fillStyle = '#e8c96a';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(poleW / 2 - 1.2, height * 0.022, poleW * 0.28, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.fill();

      const slices = Math.max(160, Math.min(300, Math.round(flagW / 1.6)));
      const sliceW = flagW / slices;
      const srcSlice = img.naturalWidth / slices;

      for (let i = 0; i < slices; i += 1) {
        const u = i / (slices - 1);
        const pinned = Math.pow(u, 1.08);
        const phase = u * Math.PI * 2.35 - t * 2.45;
        const ripple = u * Math.PI * 4.9 - t * 3.6;
        const wave = Math.sin(phase) * 0.72 + Math.sin(ripple) * 0.28;
        const yOff = wave * flagH * 0.12 * (0.05 + 0.95 * pinned);
        const dw = sliceW + 1.15;
        const dh = flagH;
        const dx = poleW + i * sliceW;
        const dy = top + yOff;

        ctx.drawImage(
          img,
          i * srcSlice,
          0,
          Math.max(0.6, srcSlice),
          img.naturalHeight,
          dx,
          dy,
          dw,
          dh
        );

        const slope = Math.cos(phase) * 0.72 + Math.cos(ripple) * 0.28;
        const light = slope * (0.08 + 0.92 * pinned);
        if (light > 0.1) {
          ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(0.2, light * 0.16)})`;
          ctx.fillRect(dx, dy, sliceW, dh);
        } else if (light < -0.12) {
          ctx.fillStyle = `rgba(22, 10, 14, ${Math.min(0.2, -light * 0.16)})`;
          ctx.fillRect(dx, dy, sliceW, dh);
        }
      }
    };

    const loop = (now) => {
      if (!running) return;
      if (!startAt) startAt = now;
      layout();
      paint(now);
      if (!reduceMotion.matches) raf = window.requestAnimationFrame(loop);
    };

    const start = () => {
      layout();
      if (reduceMotion.matches) paint(0);
      else raf = window.requestAnimationFrame(loop);
    };

    const ro = new ResizeObserver(() => layout());
    ro.observe(wrap);

    if (img.complete && img.naturalWidth) start();
    else img.onload = start;

    return () => {
      running = false;
      window.cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="viva-flag" ref={wrapRef} role="img" aria-label="Bandera de México">
      <canvas ref={canvasRef} className="viva-flag__canvas" />
    </div>
  );
}
